import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ArrowRight, Globe, GraduationCap } from 'lucide-react';

const INTL_IMG   = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda08.jpg';
const PROSP_IMG1 = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda13.jpg';
const APPLY_URL  = 'https://bilsis.hacettepe.edu.tr/oibs/foa_app/';

// Aday / uluslararası panel renkleri: sitenin bordo kimliği + ona eşlik eden koyu lacivert
const PANELS = {
  prospective: {
    overlay: 'linear-gradient(150deg, rgba(58,6,16,0.94) 0%, rgba(123,13,30,0.82) 55%, rgba(58,6,16,0.9) 100%)',
    accent: '#F4B6C0',
    chipBg: 'rgba(255,255,255,0.12)',
  },
  international: {
    overlay: 'linear-gradient(210deg, rgba(15,23,42,0.94) 0%, rgba(30,41,82,0.84) 55%, rgba(15,23,42,0.92) 100%)',
    accent: '#C7D2FE',
    chipBg: 'rgba(255,255,255,0.12)',
  },
};

/* Panel içeriği — masaüstü ve mobilde ortak */
const PanelContent = ({ icon: Icon, chip, title, accentLine, text, links, actions, accent, align = 'left' }) => (
  <div className={`flex flex-col ${align === 'right' ? 'md:items-end md:text-right' : ''}`}>
    <span className="font-display inline-flex items-center gap-2 self-start md:self-auto px-3.5 py-2 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-[0.14em] text-white border border-white/20 bg-white/10 backdrop-blur-sm">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      {chip}
    </span>

    <h2 className="font-display mt-5 font-extrabold text-white leading-[1.12] tracking-tight"
        style={{ fontSize: 'clamp(1.6rem, 2.7vw, 2.4rem)' }}>
      {title}
      <br />
      <span style={{ color: accent }}>{accentLine}</span>
    </h2>

    <p className="mt-4 text-white/80 text-[14px] sm:text-[15px] leading-relaxed max-w-md">{text}</p>

    {links && links.length > 0 && (
      <div className={`mt-5 flex flex-wrap gap-2 ${align === 'right' ? 'md:justify-end' : ''}`}>
        {links.map(l => (
          <Link key={l.href} to={l.href}
            className="font-display text-[12px] font-semibold px-3 py-1.5 rounded-full text-white/90 border border-white/25 hover:bg-white hover:text-gray-900 transition-colors">
            {l.label}
          </Link>
        ))}
      </div>
    )}

    <div className={`mt-7 flex flex-wrap gap-3 ${align === 'right' ? 'md:justify-end' : ''}`}>
      {actions}
    </div>
  </div>
);

const btnPrimary = 'font-display inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all';
const btnGhost   = 'font-display inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white text-sm font-bold hover:bg-white/10 transition-colors';

/* ── Çapraz İkili Banner ─────────────────────────────────────────────────── */
const DiagonalDualBanner = ({ isEn, prefix }) => {
  const intlHref = isEn ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler';

  const prospective = {
    icon: GraduationCap,
    chip: isEn ? 'Prospective Students' : 'Aday Öğrenciler',
    title: isEn ? 'Shape Your Future' : 'Bilimi Keşfet,',
    accentLine: isEn ? 'at the Faculty of Science' : 'Geleceğini Şekillendir',
    text: isEn
      ? 'Undergraduate and graduate education in Actuarial Science, Biology, Chemistry, Mathematics and Statistics.'
      : 'Aktüerya Bilimleri, Biyoloji, Kimya, Matematik ve İstatistik bölümlerinde lisans ve lisansüstü eğitim.',
    links: [
      { label: isEn ? 'Undergraduate' : 'Lisans Programları', href: `${prefix}/programlar/lisans` },
      { label: isEn ? 'Graduate' : 'Lisansüstü', href: `${prefix}/programlar/yuksek-lisans` },
      { label: 'Erasmus', href: `${prefix}/programlar/erasmus` },
      { label: isEn ? 'Student Clubs' : 'Öğrenci Toplulukları', href: `${prefix}/ogrenci-topluluklari` },
    ],
    accent: PANELS.prospective.accent,
    actions: (
      <Link to={`${prefix}/hakkimizda`} className={btnPrimary} style={{ color: '#7B0D1E' }}>
        {isEn ? 'Explore the Faculty' : 'Fakülteyi Keşfet'}
        <ArrowRight className="w-4 h-4" />
      </Link>
    ),
  };

  const international = {
    icon: Globe,
    chip: isEn ? 'International Students · 2026–2027' : 'Uluslararası Öğrenciler · 2026–2027',
    title: isEn ? 'Welcome to' : 'Hacettepe Üniversitesi',
    accentLine: isEn ? 'Hacettepe Faculty of Science' : "Fen Fakültesi'ne Hoş Geldiniz",
    text: isEn
      ? 'Find out about admission requirements, programmes and campus life for international students.'
      : 'Uluslararası öğrenciler için başvuru koşulları, programlar ve kampüs yaşamı hakkında bilgi alın.',
    accent: PANELS.international.accent,
    actions: (
      <>
        <a href={APPLY_URL} target="_blank" rel="noopener noreferrer" className={btnPrimary} style={{ color: '#1E293B' }}>
          {isEn ? 'Apply Now' : 'Hemen Başvur'}
          <ArrowUpRight className="w-4 h-4" />
        </a>
        <Link to={intlHref} className={btnGhost}>
          {isEn ? 'Learn More' : 'Detaylı Bilgi'}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </>
    ),
  };

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <motion.div {...fade(0)} className="relative overflow-hidden rounded-3xl mb-14 shadow-2xl">

      {/* ══════════ MASAÜSTÜ: çapraz kesimli iki panel ══════════ */}
      <div className="hidden md:block relative min-h-[560px]">
        {/* Sol arka plan */}
        <div className="absolute inset-0" style={{ clipPath: 'polygon(0 0, 56% 0, 46% 100%, 0 100%)' }}>
          <img src={PROSP_IMG1} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: PANELS.prospective.overlay }} />
        </div>
        {/* Sağ arka plan */}
        <div className="absolute inset-0" style={{ clipPath: 'polygon(56% 0, 100% 0, 100% 100%, 46% 100%)' }}>
          <img src={INTL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0" style={{ background: PANELS.international.overlay }} />
        </div>
        {/* İnce ayırıcı çizgi */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" aria-hidden="true">
          <line x1="56" y1="0" x2="46" y2="100" stroke="rgba(255,255,255,0.35)" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
        </svg>

        {/* İçerik: iki sütun, çaprazın iki yanında güvenli genişlikte */}
        <div className="relative z-10 min-h-[560px] flex items-end justify-between gap-10 p-10 lg:p-14">
          <motion.div {...fade(0.1)} className="w-[40%]">
            <PanelContent {...prospective} />
          </motion.div>
          <motion.div {...fade(0.2)} className="w-[38%]">
            <PanelContent {...international} align="right" />
          </motion.div>
        </div>
      </div>

      {/* ══════════ MOBİL: alt alta iki kart ══════════ */}
      <div className="md:hidden">
        <div className="relative">
          <img src={PROSP_IMG1} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: PANELS.prospective.overlay }} />
          <div className="relative px-6 py-10">
            <PanelContent {...prospective} />
          </div>
        </div>
        <div className="relative border-t border-white/15">
          <img src={INTL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0" style={{ background: PANELS.international.overlay }} />
          <div className="relative px-6 py-10">
            <PanelContent {...international} />
          </div>
        </div>
      </div>

    </motion.div>
  );
};


/* ── Kart Bileşeni ─────────────────────────────────────────────────────────── */
const Card = ({ title, tag, desc, image, href, external, subLinks = [], accent = '#fff', exploreLabel = 'Keşfet' }) => {
  const isExternal = external || (href && href.startsWith('http'));

  const inner = (
    <div className="relative group h-full overflow-hidden rounded-3xl cursor-pointer">
      <img
        src={image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.06]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" style={{ height: '55%', top: 'auto' }} />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-7">
        <span
          className="text-[9px] font-black uppercase tracking-[0.25em] mb-3 block opacity-70"
          style={{ color: accent, textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}
        >
          {tag}
        </span>

        <h3
          className="text-xl md:text-2xl font-black text-white leading-tight transition-transform duration-400 group-hover:-translate-y-2"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.95)' }}
        >
          {title}
        </h3>

        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 overflow-hidden">
          <div className="overflow-hidden">
            {desc && (
              <p className="text-sm text-white/65 mt-3 leading-relaxed line-clamp-2">{desc}</p>
            )}

            {subLinks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {subLinks.slice(0, 5).map((l, i) => (
                  l.href.startsWith('http') ? (
                    <a key={i} href={l.href} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </a>
                  ) : (
                    <Link key={i} to={l.href} onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </Link>
                  )
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5 mt-4 text-white text-xs font-bold tracking-wide">
              <span>{exploreLabel}</span>
              <ArrowUpRight size={13} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!href) return inner;
  if (isExternal)
    return <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>;
  return <Link to={href} className="block h-full">{inner}</Link>;
};

/* ── Ana Bileşen ───────────────────────────────────────────────────────────── */
const ShowcaseGrid = () => {
  const { t, i18n } = useTranslation();
  const prefix = i18n.language === 'en' ? '/en' : '';

  const isEn = i18n.language === 'en';

  const cards = [
    {
      tag: isEn ? 'Academic' : 'Akademik',
      title: t('showcase.departments'),
      desc: isEn
        ? 'Five world-class departments: Actuarial Science, Biology, Chemistry, Mathematics and Statistics.'
        : 'Aktüerya, Biyoloji, Kimya, Matematik ve İstatistik bölümleriyle bilimin öncüsü.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/MATEMAT%C4%B0K.jpg',
      subLinks: [
        { text: isEn ? 'Actuarial Sci.' : 'Aktüerya',   href: 'https://aktuerya.hacettepe.edu.tr/' },
        { text: isEn ? 'Biology'        : 'Biyoloji',   href: 'https://biology.hacettepe.edu.tr/' },
        { text: isEn ? 'Chemistry'      : 'Kimya',      href: 'https://chem.hacettepe.edu.tr/' },
        { text: isEn ? 'Mathematics'    : 'Matematik',  href: 'https://mat.hacettepe.edu.tr/' },
        { text: isEn ? 'Statistics'     : 'İstatistik', href: 'https://stat.hacettepe.edu.tr/' },
      ],
    },
    {
      tag: isEn ? 'Education' : 'Eğitim',
      title: t('showcase.academicPrograms'),
      desc: isEn
        ? 'Undergraduate, graduate and doctoral programmes for an academic career.'
        : 'Lisans, yüksek lisans ve doktora programlarıyla akademik kariyer.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf.jpg',
      href: prefix + '/programlar/lisans',
      subLinks: [
        { text: isEn ? 'Undergraduate' : 'Lisans',        href: prefix + '/programlar/lisans' },
        { text: isEn ? 'Master\'s'     : 'Yüksek Lisans', href: prefix + '/programlar/yuksek-lisans' },
        { text: isEn ? 'Doctorate'     : 'Doktora',       href: prefix + '/programlar/doktora' },
        { text: 'Erasmus',                                 href: prefix + '/programlar/erasmus' },
      ],
    },
    {
      tag: isEn ? 'Research' : 'Araştırma',
      title: t('showcase.researchLabs'),
      desc: t('showcase.labsDesc'),
      image: 'https://lab.hacettepe.edu.tr/fs_/labs/hucrefizyolojisi1.jpg',
      href: prefix + '/arastirma/laboratuvarlar',
    },
    {
      tag: isEn ? 'Faculty' : 'Kadro',
      title: t('showcase.faculty'),
      desc: t('showcase.facultyDesc'),
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf1.jpg',
      href: prefix + '/fakulte-personeli/ogretim-uyeleri',
    },
    {
      tag: isEn ? 'Publication' : 'Yayın',
      title: t('showcase.journals'),
      desc: t('showcase.journalsDesc'),
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80',
      href: prefix + '/arastirma/dergiler',
    },
    {
      tag: isEn ? 'Research & Innovation' : 'Araştırma & İnovasyon',
      title: t('showcase.ongoingProjects'),
      desc: t('showcase.projectsDesc'),
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=1600&q=80',
      href: prefix + '/arastirma/devam-eden-projelerimiz',
    },
  ];

  return (
    <section className="bg-white py-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Çapraz İkili Banner: Aday Öğrenciler + Uluslararası ── */}
        <DiagonalDualBanner isEn={isEn} prefix={prefix} />

        {/* ── Başlık ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#7B0D1E] mb-2.5">
              {isEn ? 'Hacettepe University · Faculty of Science' : 'Hacettepe Üniversitesi · Fen Fakültesi'}
            </p>
            <h2 className="text-3xl md:text-[2.25rem] font-black text-gray-900 leading-tight">
              {t('showcase.title')}
            </h2>
          </div>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed md:text-right md:pb-1">
            {t('showcase.subtitle')}
          </p>
        </motion.div>

        {/* ── 3×2 Eşit Grid ── */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {cards.map((card, i) => (
            <div key={i} style={{ height: '280px' }}>
              <Card {...card} exploreLabel={isEn ? 'Explore' : 'Keşfet'} />
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ShowcaseGrid;
